import { useEffect, useMemo, useState } from 'react';
import { Api } from '../application/panelApi';
import { ExportCsvModal } from '../components/ExportCsvModal';
import { positionsCsv, type CsvExport } from '../features/csvExport';
import {
  currentGeoPosition,
  dayLabel,
  formatRank,
  geoValues,
  hostMatchesSite,
  hourLabel,
  keywordValues,
  periodTitle,
  pickVisibleGeos,
  probeLabel,
  rankTone,
  shortGeoLabel,
  trimEmptyDays,
  trimEmptyHours,
  type RankGeoRow,
  type RankKeywordRow,
  type RankMatrix,
} from '../domain/ranks';
import { nf, plural } from '../lib/format';
import { useApp } from '../store/AppContext';
import type { PfTask } from '../domain/positions';

type Grain = 'day' | 'hour';
type Selection = { keyword: RankKeywordRow; geo: RankGeoRow; key?: string };

function RankSpark({ keys, values }: { keys: string[]; values: Record<string, number | null> }) {
  const points = keys
    .map((key, i) => {
      const value = values[key];
      if (value == null || value <= 0) return null;
      return { i, value };
    })
    .filter((p): p is { i: number; value: number } => p != null);
  if (points.length < 2) return <span className="rk-spark-empty">мало точек</span>;
  const low = Math.min(...points.map((p) => p.value));
  const spread = Math.max(Math.max(...points.map((p) => p.value)) - low, 1);
  const last = keys.length - 1 || 1;
  const coords = points
    .map((p) => `${4 + (p.i * 152) / last},${4 + ((p.value - low) / spread) * 46}`)
    .join(' ');
  return (
    <svg className="rk-spark" viewBox="0 0 160 54" role="img" aria-label="Динамика позиции">
      <polyline points={coords} fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

function DayCells({
  keys,
  values,
  activeKey,
  grain,
  onPick,
}: {
  keys: string[];
  values: Record<string, number | null>;
  activeKey?: string;
  grain: Grain;
  onPick?: (key: string) => void;
}) {
  return (
    <>
      {keys.map((key) => {
        const known = key in values;
        const value = values[key];
        const label = known ? formatRank(value) : '—';
        return (
          <button
            type="button"
            key={key}
            className={`rk-cell rk-${rankTone(known ? value : undefined)}${activeKey === key ? ' is-focus' : ''}`}
            title={`${periodTitle(key, grain)} · ${label}`}
            onClick={(event) => {
              event.stopPropagation();
              onPick?.(key);
            }}
          >
            {label}
          </button>
        );
      })}
    </>
  );
}

export function PositionsPage() {
  const { tasks: rawTasks, ui, refreshTasks } = useApp();
  const tasks = rawTasks as PfTask[];
  const eng = ui.searchEngine === 'google' ? 'google' : 'bing';
  const [filter, setFilter] = useState('');
  const [taskId, setTaskId] = useState('all');
  const [grain, setGrain] = useState<Grain>('day');
  const [focusDay, setFocusDay] = useState<string | null>(null);
  const [open, setOpen] = useState<Record<string, boolean>>({});
  const [showAllGeos, setShowAllGeos] = useState<Record<string, boolean>>({});
  const [picked, setPicked] = useState<Selection | null>(null);
  const [matrix, setMatrix] = useState<RankMatrix | null>(null);
  const [exp, setExp] = useState<CsvExport | null>(null);
  const [probing, setProbing] = useState(false);

  const engineTasks = useMemo(
    () => tasks.filter((t) => t.lane !== 'neuro' && (t.searchEngine || 'bing') === eng),
    [tasks, eng],
  );
  const effectiveTask = engineTasks.some((t) => t.id === taskId) ? taskId : 'all';

  useEffect(() => {
    const load = () => void refreshTasks().catch(() => undefined);
    load();
    const timer = window.setInterval(load, 15000);
    return () => window.clearInterval(timer);
  }, [refreshTasks]);

  useEffect(() => {
    let alive = true;
    const load = () => {
      void Api.ranks.list({ days: 14, engine: eng, taskId: effectiveTask }).then((next) => {
        if (alive) setMatrix(next);
      }).catch(() => undefined);
    };
    load();
    const timer = window.setInterval(load, 15000);
    return () => {
      alive = false;
      window.clearInterval(timer);
    };
  }, [eng, effectiveTask]);

  const rows = useMemo(() => {
    const q = filter.trim().toLowerCase();
    return (matrix?.keywords ?? []).filter((row) => (
      row.geos.length
      && (!q || row.keyword.toLowerCase().includes(q) || row.site.toLowerCase().includes(q))
    ));
  }, [matrix, filter]);

  const days = useMemo(
    () => trimEmptyDays(matrix?.days ?? [], rows),
    [matrix, rows],
  );
  const allHours = useMemo(
    () => trimEmptyHours(matrix?.hours ?? [], rows),
    [matrix, rows],
  );
  const hours = useMemo(() => {
    if (focusDay) return allHours.filter((key) => key.startsWith(focusDay));
    return allHours.slice(-24);
  }, [allHours, focusDay]);
  const columns = grain === 'hour' ? hours : days;

  useEffect(() => {
    setPicked((current) => {
      if (!current) return current;
      const next = rows.find((row) => row.taskId === current.keyword.taskId && row.keyword === current.keyword.keyword);
      const geo = next?.geos.find((g) => g.id === current.geo.id);
      if (next && geo) return { keyword: next, geo, key: current.key };
      return next ? current : null;
    });
  }, [rows]);

  const toggleKeyword = (row: RankKeywordRow) => {
    const key = `${row.taskId}:${row.keyword}`;
    const next = !open[key];
    setOpen((prev) => ({ ...prev, [key]: next }));
    if (!next && picked?.keyword.taskId === row.taskId && picked.keyword.keyword === row.keyword) {
      setPicked(null);
    }
  };

  const selectGeo = (keyword: RankKeywordRow, geo: RankGeoRow, key?: string) => {
    setPicked({ keyword, geo, key });
    setOpen((prev) => ({ ...prev, [`${keyword.taskId}:${keyword.keyword}`]: true }));
  };

  const openDay = (date: string) => {
    setFocusDay(date);
    setGrain('hour');
  };

  const runProbe = () => {
    if (probing) return;
    setProbing(true);
    void Api.ranks.probe()
      .then((body) => {
        setMatrix((current) => current ? { ...current, probe: body.probe ?? current.probe } : current);
      })
      .catch(() => undefined)
      .finally(() => setProbing(false));
  };

  return (
    <section id="page-positions" className="st-positions-section panel rk-page" aria-labelledby="st-positions-title">
      <div className="st-sites-head rk-toolbar">
        <h2 id="st-positions-title">Позиции <span className="pf-count">{rows.length}</span></h2>
        <div className="st-tools rk-tools">
          <div className="rk-grain" role="tablist" aria-label="Шаг времени">
            <button type="button" role="tab" aria-selected={grain === 'day'} className={grain === 'day' ? 'is-on' : ''} onClick={() => { setGrain('day'); setFocusDay(null); }}>
              Дни
            </button>
            <button type="button" role="tab" aria-selected={grain === 'hour'} className={grain === 'hour' ? 'is-on' : ''} onClick={() => setGrain('hour')}>
              Часы
            </button>
          </div>
          <input id="poFilter" type="text" aria-label="Поиск по запросу или сайту" placeholder="Поиск по запросу или сайту" spellCheck={false} value={filter} onChange={(e) => setFilter(e.target.value)} />
          <select id="poTask" aria-label="Сайт" value={effectiveTask} onChange={(e) => setTaskId(e.target.value)}>
            <option value="all">Все сайты</option>
            {engineTasks.map((t) => <option key={t.id} value={t.id}>{t.site}</option>)}
          </select>
          <button type="button" className="btn" disabled={probing} onClick={runProbe}>
            {probing ? 'Запускаю…' : 'Проверить сейчас'}
          </button>
          <button type="button" className="btn" data-export="positions" onClick={() => setExp(positionsCsv(tasks))}>
            Экспорт CSV
          </button>
        </div>
        <p className="rk-status">
          {probeLabel(matrix?.probe)}
          {grain === 'hour' && focusDay ? ` · ${periodTitle(focusDay, 'day')}` : ''}
          {grain === 'hour' && focusDay && (
            <button type="button" className="rk-status-link" onClick={() => { setGrain('day'); setFocusDay(null); }}>все дни</button>
          )}
        </p>
      </div>
      <div className={`rk-layout${picked ? ' has-side' : ''}`}>
        <div className="rk-table-wrap" style={{ ['--rk-days' as string]: String(Math.max(columns.length, 1)) }}>
          <div className="rk-head">
            <span>Запрос</span>
            {columns.map((key) => (
              grain === 'day' ? (
                <button type="button" key={key} className={`rk-day${focusDay === key ? ' is-focus' : ''}`} onClick={() => openDay(key)} title={`Часы за ${periodTitle(key, 'day')}`}>
                  {dayLabel(key)}
                </button>
              ) : (
                <span key={key} className="rk-day">{hourLabel(key)}</span>
              )
            ))}
          </div>
          <div className="rk-body">
            {rows.length ? rows.map((row) => {
              const key = `${row.taskId}:${row.keyword}`;
              const expanded = Boolean(open[key]);
              const allGeos = Boolean(showAllGeos[key]);
              const { shown, hidden } = pickVisibleGeos(row.geos, days, allGeos);
              const active = picked?.keyword.keyword === row.keyword && picked.keyword.taskId === row.taskId;
              return (
                <div className={`rk-block${active ? ' is-active' : ''}${expanded ? ' is-open' : ''}`} key={key}>
                  <div className="rk-key">
                    <button type="button" className="rk-key-meta" aria-expanded={expanded} onClick={() => toggleKeyword(row)}>
                      <strong>{row.keyword}</strong>
                      <small>{row.site} · {nf.format(row.searches)} / {nf.format(row.clicks)}</small>
                    </button>
                    <DayCells
                      keys={columns}
                      values={keywordValues(row, grain)}
                      grain={grain}
                      onPick={(cell) => {
                        if (grain === 'day') openDay(cell);
                        const geo = shown[0] ?? row.geos[0];
                        if (geo) selectGeo(row, geo, cell);
                      }}
                    />
                  </div>
                  {expanded && shown.map((geo) => {
                    const selected = picked?.geo.id === geo.id;
                    return (
                      <div className={`rk-geo${selected ? ' is-selected' : ''}`} key={geo.id}>
                        <button type="button" className="rk-geo-meta" onClick={() => selectGeo(row, geo)}>
                          <strong>{shortGeoLabel(geo)}</strong>
                        </button>
                        <DayCells
                          keys={columns}
                          values={geoValues(geo, grain)}
                          activeKey={selected ? picked?.key : undefined}
                          grain={grain}
                          onPick={(cell) => selectGeo(row, geo, cell)}
                        />
                      </div>
                    );
                  })}
                  {expanded && hidden > 0 && (
                    <button
                      type="button"
                      className="rk-more"
                      onClick={() => setShowAllGeos((prev) => ({ ...prev, [key]: true }))}
                    >
                      ещё {hidden} {plural(hidden, 'страна', 'страны', 'стран')}
                    </button>
                  )}
                  {expanded && allGeos && row.geos.length > 8 && (
                    <button
                      type="button"
                      className="rk-more"
                      onClick={() => setShowAllGeos((prev) => ({ ...prev, [key]: false }))}
                    >
                      свернуть страны
                    </button>
                  )}
                </div>
              );
            }) : <div className="empty">По выбранным условиям замеров нет — они появятся после почасовой проверки уникальным браузером</div>}
          </div>
        </div>
        {picked ? (
          <aside className="rk-side" aria-label="Выдача Bing">
            <div className="rk-side-head">
              <div>
                <strong>{picked.keyword.keyword}</strong>
                <small>{shortGeoLabel(picked.geo)}</small>
                <em>
                  сейчас {formatRank(currentGeoPosition(picked.geo, days, picked.keyword.site))}
                  {picked.key ? ` · ${periodTitle(picked.key, grain)}` : ''}
                </em>
              </div>
              <button type="button" className="rk-side-close" aria-label="Закрыть выдачу" onClick={() => setPicked(null)}>
                ×
              </button>
            </div>
            <RankSpark keys={columns} values={geoValues(picked.geo, grain)} />
            <div className="rk-serp">
              <div className="rk-serp-head">
                <span>Как в {eng === 'google' ? 'Google' : 'Bing'}</span>
                {picked.geo.serp && <time dateTime={picked.geo.serp.date}>{dayLabel(picked.geo.serp.date)}</time>}
              </div>
              {picked.geo.serp?.html ? (
                <div className="rk-serp-live">
                  <div className="rk-serp-fit">
                    <iframe
                      key={picked.geo.serp.date}
                      title={`Выдача ${eng === 'google' ? 'Google' : 'Bing'}`}
                      sandbox=""
                      referrerPolicy="no-referrer"
                      srcDoc={picked.geo.serp.html}
                    />
                  </div>
                </div>
              ) : picked.geo.serp?.items.length ? picked.geo.serp.items.map((card) => {
                const mine = hostMatchesSite(card.host, picked.keyword.site);
                return (
                  <article key={`${card.pos}:${card.url}`} className={`rk-card${mine ? ' is-target' : ''}`}>
                    <span className="rk-card-pos">{card.pos}</span>
                    <div>
                      <a href={card.url} target="_blank" rel="noreferrer">{card.title || card.host}</a>
                      <small>{card.host}</small>
                      {card.snippet && <p>{card.snippet}</p>}
                    </div>
                  </article>
                );
              }) : <p className="rk-serp-empty">Снимок выдачи ещё не пришёл. Он пишется при почасовой проверке или целевом клике.</p>}
            </div>
          </aside>
        ) : null}
      </div>
      {exp && <ExportCsvModal name={exp.name} csv={exp.csv} onClose={() => setExp(null)} />}
    </section>
  );
}
